/*
 * flashmeeting-be
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */

import React, { useEffect, useState } from 'react';
import Page from '@common_components/global/page.component';
import { useDispatch, useSelector } from 'react-redux';
import DiaryNoteService from '../services/diaryNote.service';
import { useTranslation } from 'react-i18next';

import type { DiaryNoteFilter, DiaryNoteResponse } from '../interfaces/diaryNote.interface';
import { GenericTable } from 'components/table.component';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import { EventManagerIcon } from '@common_components/icons/eventManager.icon';
import { EventService } from 'services/events.service';
import { Client, ClientIcon, CommonClientService, CommonGlobalService, CommonSectorService, CommonSiteService, CommonUtilsService, dayjs, FilterIcon, FORMAT_DATE_API, GroupedOption, MultiSelect, Option, RootState, Sector, SectorIcon, Site, SiteIcon, ThemeIcon, usePersistantState, UUID } from '@umanforge/common';
import { KEY } from 'constants/local_storage_key.constant';
import DatePickerInput from '@common_components/inputs/datePickerInput.component';
import { NewsDeleteIcon } from '@common_components/icons/newsDelete.icon';
import { NoDataAvailableIcon } from '@common_components/icons/noDataAvailable';
import DataChecker from 'components/common/dataChecker';
import { EventManagerService } from 'services/eventManagers.service';
import { ThemeService } from 'services/themes.service';
import { ApiTheme } from 'interfaces/theme.interface';
import { EventManagerWithPositionBySector } from 'interfaces/eventManager.interface';
import { Loading } from '@common_components/loading.component';
import { FilterPanel } from 'components/FilterSection/filterPanel.component';
import { FilterButton } from 'components/FilterSection/filterButton.component';
import { FilterProvider } from 'components/FilterSection/filterProvider';

const FILTER_WIDTH = 'w-[350px]';

const DiaryNoteView: React.FC = () => {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const DEFAULT_STATUS: { label: string, id: 'all' | 'enabled' | 'disabled' }[] = [
    { label: t( 'common:all' ), id: 'all' },
    { label: t( 'common:enabled' ), id: 'enabled' },
    { label: t( 'common:disabled' ), id: 'disabled' },
  ];

  const [ defaultSelectedClientId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_CLIENT_ID, undefined );

  const [ selectedClients, setSelectedClients ] = useState<Option<Client>[]>( [] );
  const [ clientsOption, setClientsOption ] = useState<Option<Client>[]>( [] );
  const [ allClients, setAllClients ] = useState<Client[]>( [] );

  const [ selectedSites, setSelectedSites ] = useState<Option<Site>[]>( [] );
  const [ sitesOption, setSitesOption ] = useState<GroupedOption<Site>[]>( [] );
  const [ allSites, setAllSites ] = useState<Site[]>( [] );

  const [ selectedSectors, setSelectedSectors ] = useState<Option<Sector>[]>( [] );
  const [ sectorsOption, setSectorsOption ] = useState<GroupedOption<Sector>[]>( [] );
  const [ allSectors, setAllSectors ] = useState<Sector[]>( [] );

  const [ selectedEventManagers, setSelectedEventManagers ] = useState<Option<EventManagerWithPositionBySector>[]>( [] );
  const [ eventManagersOption, setEventManagersOption ] = useState<GroupedOption<EventManagerWithPositionBySector>[]>( [] );

  const [ selectedThemes, setSelectedThemes ] = useState<Option<ApiTheme>[]>( [] );
  const [ themesOptions, setThemesOptions ] = useState<GroupedOption<ApiTheme>[]>( [] );

  const [ filterStatus, setFilterStatus ] = useState< 'all' | 'enabled' | 'disabled' >( DEFAULT_STATUS[ 0 ].id );
  const [ filterDate, setFilterDate ] = useState<Date[]>( [] );

  const [ renderList, setRenderList ] = useState<number>( 0 );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  useEffect( () => {
    if ( defaultSelectedClientId ) {
      init();
    }
  }, [ defaultSelectedClientId ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const fetchSites = async () => {
        let updatedSiteOptions: Option<Site>[] = [];
        if ( selectedClients.length > 0 ) {
          updatedSiteOptions = CommonGlobalService.toOptions( await CommonSiteService.getAllowedSitesByClientsId( selectedClients.map( client => { return client.object.id; } ), token, dispatch ) );
        }
        const groupedSites: GroupedOption<Site>[] = [];
        for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( updatedSiteOptions, option => { return option.object.clientId; } ) ) ) {
          const optionSites = options ?? [];
          CommonUtilsService.sortByKey( optionSites, 'label', 'asc' );
          groupedSites.push( {
            label: selectedClients.find( client => { return client.object.id === clientId; } )?.object.name ?? t( 'common:error.missing_client' ),
            options: optionSites
          } );
        }
        setSitesOption( groupedSites );
      };
      fetchSites();
    }
  }, [ selectedClients ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const sites = sitesOption
        .map( site => { return site.options; } )
        .flat();
      const filteredSelectedSites = selectedSites.filter( selectedSite => {
        return sites.some( site => {
          return site.object.id === selectedSite.object.id;
        } );
      } );
      setSelectedSites( filteredSelectedSites );
    }
  }, [ sitesOption ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      const fetchSectors = async () => {
        let updatedSectorOptions: Option<Sector>[] = [];
        if ( selectedSites.length > 0 ) {
          updatedSectorOptions = CommonGlobalService.toOptions( await CommonSectorService.getAllowedSectorsBySitesId( selectedSites.map( site => { return site.object.id; } ), token, dispatch ) );
        }
        const groupedSectors: GroupedOption<Sector>[] = [];
        for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( updatedSectorOptions, option => { return option.object.siteId; } ) ) ) {
          const optionSectors = options ?? [];
          CommonUtilsService.sortByKey( optionSectors, 'label', 'asc' );
          groupedSectors.push( {
            label: selectedSites.find( site => { return site.object.id === siteId; } )?.object.name ?? t( 'common:error.missing_site' ),
            options: optionSectors
          } );
        }
        setSectorsOption( groupedSectors );
      };
      fetchSectors();
    }
  }, [ selectedSites ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const sectors = sectorsOption
        .map( sector => { return sector.options; } )
        .flat();
      const filteredSelectedSectors = selectedSectors.filter( selectedSector => {
        return sectors.some( sector => {
          return sector.object.id === selectedSector.object.id;
        } );
      } );
      setSelectedSectors( filteredSelectedSectors );
    }
  }, [ sectorsOption ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      const fetchEventManagers = async () => {
        let updatedEventManagerOptions: Option<EventManagerWithPositionBySector>[] = [];
        if ( selectedSectors.length > 0 ) {
          updatedEventManagerOptions = CommonGlobalService.toOptions( await EventManagerService.getTablesBySectorIDs( selectedSectors.map( sector => { return sector.object.id; } ), token, dispatch ) );
        }
        const groupedEventManagers: GroupedOption<EventManagerWithPositionBySector>[] = [];
        for ( const [ sectorId, options ] of Object.entries( CommonGlobalService.groupBy( updatedEventManagerOptions, option => { return option.object.sectorId; } ) ) ) {
          const optionEventManagers = options ?? [];
          CommonUtilsService.sortByKey( optionEventManagers, 'label', 'asc' );
          groupedEventManagers.push( {
            label: selectedSectors.find( site => { return site.object.id === sectorId; } )?.object.name ?? t( 'common:error.missing_sector' ),
            options: optionEventManagers
          } );
        }
        setEventManagersOption( groupedEventManagers );
      };
      fetchEventManagers();
    }
  }, [ selectedSectors ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const eventManagers = eventManagersOption
        .map( eventManager => { return eventManager.options; } )
        .flat();
      const filteredSelectedEventManagers = selectedEventManagers.filter( selectedEventManager => {
        return eventManagers.some( eventManager => {
          return eventManager.object.id === selectedEventManager.object.id;
        } );
      } );
      setSelectedEventManagers( filteredSelectedEventManagers );
    }
  }, [ eventManagersOption ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      const fetchThemes = async () => {
        let updatedThemeOptions: Option<ApiTheme>[] = [];
        if ( selectedEventManagers.length > 0 ) {
          updatedThemeOptions = CommonGlobalService.toOptions( await ThemeService.getThemesByEventManager( selectedEventManagers.map( eventManager => { return eventManager.object.id; } ), token, dispatch ) );
        }
        const groupedThemes: GroupedOption<ApiTheme>[] = [];
        for ( const [ eventManagerId, options ] of Object.entries( CommonGlobalService.groupBy( updatedThemeOptions, option => { return option.object.eventManagerId ?? ''; } ) ) ) {
          const optionThemes = options ?? [];
          CommonUtilsService.sortByKey( optionThemes, 'label', 'asc' );
          groupedThemes.push( {
            label: selectedEventManagers.find( site => { return site.object.id === eventManagerId; } )?.object.name ?? t( 'translation:missing_event_manager' ),
            options: optionThemes
          } );
        }
        setThemesOptions( groupedThemes );
      };
      fetchThemes();
    }
  }, [ selectedEventManagers ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const themes = themesOptions
        .map( theme => { return theme.options; } )
        .flat();
      const filteredSelectedThemes = selectedThemes.filter( selectedTheme => {
        return themes.some( theme => {
          return theme.object.id === selectedTheme.object.id;
        } );
      } );
      setSelectedThemes( filteredSelectedThemes );
    }
  }, [ themesOptions ] );

  useEffect( () => {
    if ( !isFirstRender && selectedClients.length > 0 ) {
      setRenderList( renderList + 1 );
    }
  }, [ isFirstRender, selectedClients, selectedSites, selectedSectors, selectedEventManagers, selectedThemes, filterStatus, filterDate ] );

  const init = async () => {
    const clientsFetched = await CommonClientService.getAllowedClients( null, token, dispatch );
    const updatedClientsOptions = CommonGlobalService.toOptions( clientsFetched );
    setClientsOption( updatedClientsOptions );
    setAllClients( clientsFetched );
    let updatedClients: Option<Client>[] = [];
    const defaultSelectedClient = updatedClientsOptions.find( client => { return defaultSelectedClientId === client.object.id; } );
    if ( defaultSelectedClient ) {
      if ( !selectedClients.some( client => { return client.object.id === defaultSelectedClient.object.id; } ) ) {
        updatedClients = [ defaultSelectedClient, ...selectedClients ];
        setSelectedClients( updatedClients );
      }
    }

    let sitesFetched: Site[] = [];
    if ( clientsFetched.length > 0 ) {
      const allSitesUpdated = await CommonSiteService.getAllowedSitesByClientsId( clientsFetched.map( client => { return client.id; } ), token, dispatch );
      setAllSites( allSitesUpdated );
      if ( updatedClients.length > 0 ) {
        sitesFetched = await CommonSiteService.getAllowedSitesByClientsId( updatedClients.map( client => { return client.object.id; } ), token, dispatch );
      }
    } else {
      setAllSites( [] );
    }
    const groupedSites: GroupedOption<Site>[] = [];
    for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( CommonGlobalService.toOptions( sitesFetched ), option => { return option.object.clientId; } ) ) ) {
      const optionSites = options ?? [];
      CommonUtilsService.sortByKey( optionSites, 'label', 'asc' );
      groupedSites.push( {
        label: updatedClients.find( client => { return client.object.id === clientId; } )?.object.name ?? t( 'common:error.missing_client' ),
        options: optionSites
      } );
    }
    setSitesOption( groupedSites );
    let updatedSites: Option<Site>[] = [];
    if ( groupedSites.length === 1 && groupedSites[ 0 ].options.length === 1 ) {
      updatedSites = [ groupedSites[ 0 ].options[ 0 ] ];
      setSelectedSites( updatedSites );
    }

    let sectorsFetched: Sector[] = [];
    if ( sitesFetched.length > 0 ) {
      const allSectorsUpdated = await CommonSectorService.getAllowedSectorsBySitesId( sitesFetched.map( site => { return site.id; } ), token, dispatch );
      setAllSectors( allSectorsUpdated );
      if ( updatedSites.length > 0 ) {
        sectorsFetched = await CommonSectorService.getAllowedSectorsBySitesId( updatedSites.map( client => { return client.object.id; } ), token, dispatch );
      }
    } else {
      setAllSectors( [] );
    }
    const groupedSectors: GroupedOption<Sector>[] = [];
    for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( CommonGlobalService.toOptions( sectorsFetched ), option => { return option.object.siteId; } ) ) ) {
      const optionSectors = options ?? [];
      CommonUtilsService.sortByKey( optionSectors, 'label', 'asc' );
      groupedSectors.push( {
        label: updatedSites.find( site => { return site.object.id === siteId; } )?.object.name ?? t( 'common:error.missing_site' ),
        options: optionSectors
      } );
    }
    setSectorsOption( groupedSectors );
    let updatedSectors: Option<Sector>[] = [];
    if ( groupedSectors.length === 1 && groupedSectors[ 0 ].options.length === 1 ) {
      updatedSectors = [ groupedSectors[ 0 ].options[ 0 ] ];
      setSelectedSectors( updatedSectors );
    }

    let eventManagersFetched: EventManagerWithPositionBySector[] = [];
    if ( updatedSectors.length > 0 ) {
      eventManagersFetched = await EventManagerService.getTablesBySectorIDs( updatedSectors.map( client => { return client.object.id; } ), token, dispatch );
    }
    const groupedEventManagers: GroupedOption<EventManagerWithPositionBySector>[] = [];
    for ( const [ sectorId, options ] of Object.entries( CommonGlobalService.groupBy( CommonGlobalService.toOptions( eventManagersFetched ), option => { return option.object.sectorId; } ) ) ) {
      const optionEventManagers = options ?? [];
      CommonUtilsService.sortByKey( optionEventManagers, 'label', 'asc' );
      groupedEventManagers.push( {
        label: updatedSectors.find( sector => { return sector.object.id === sectorId; } )?.object.name ?? t( 'common:error.missing_sector' ),
        options: optionEventManagers
      } );
    }
    setEventManagersOption( groupedEventManagers );
    let updatedEventManagers: Option<EventManagerWithPositionBySector>[] = [];
    if ( groupedEventManagers.length === 1 && groupedEventManagers[ 0 ].options.length === 1 ) {
      updatedEventManagers = [ groupedEventManagers[ 0 ].options[ 0 ] ];
      setSelectedEventManagers( updatedEventManagers );
    }

    let updatedThemeOptions: Option<ApiTheme>[] = [];
    if ( updatedEventManagers.length > 0 ) {
      updatedThemeOptions = CommonGlobalService.toOptions( await ThemeService.getThemesByEventManager( updatedEventManagers.map( client => { return client.object.id; } ), token, dispatch ) );
    }
    const groupedThemes: GroupedOption<ApiTheme>[] = [];
    for ( const [ eventManagerId, options ] of Object.entries( CommonGlobalService.groupBy( updatedThemeOptions, option => { return option.object.eventManagerId ?? ''; } ) ) ) {
      const optionThemes = options ?? [];
      CommonUtilsService.sortByKey( optionThemes, 'label', 'asc' );
      groupedThemes.push( {
        label: updatedEventManagers.find( eventManager => { return eventManager.object.id === eventManagerId; } )?.object.name ?? t( 'translation:missing_event_manager' ),
        options: optionThemes
      } );
    }
    setThemesOptions( groupedThemes );
    if ( groupedThemes.length === 1 && groupedThemes[ 0 ].options.length === 1 ) {
      setSelectedThemes( [ groupedThemes[ 0 ].options[ 0 ] ] );
    }

    setIsFirstRender( false );
  };

  const fetchDiaryNotes = async ( page: number, step: number, { key, order }: { key: string, order: ( 'asc' | 'desc' ) } ) => {
    let response: { total: number, datas: DiaryNoteResponse[] } = { total: 0, datas: [] };
    if ( selectedClients.length > 0 ) {
      const filter: DiaryNoteFilter = {
        page, step, order: { key, order },
        ...( selectedClients.length > 0 && { clientIds: selectedClients.map( client => { return client.object.id; } ) } ),
        ...( selectedSites.length > 0 && { siteIds: selectedSites.map( site => { return site.object.id; } ) } ),
        ...( selectedSectors.length > 0 && { sectorIds: selectedSectors.map( sector => { return sector.object.id; } ) } ),
        ...( selectedEventManagers.length > 0 && { eventManagerIds: selectedEventManagers.map( eventmanager => { return eventmanager.object.id; } ) } ),
        ...( selectedThemes.length > 0 && { themeIds: selectedThemes.map( theme => { return theme.object.id; } ) } ),
        ...( filterDate.length === 2 && { createdDateRange: { start: dayjs( filterDate[ 0 ] ).format( FORMAT_DATE_API ), end: dayjs( filterDate[ 1 ] ).format( FORMAT_DATE_API ) } } ),
        ...( filterStatus != null && { status: filterStatus } ),
      };

      response = await DiaryNoteService.getDiaryNotesByFilter( filter, token, dispatch );
      response.datas = response.datas.map( note => { return { ...note, theme: { name: note.themeName, color: note.themeColor } }; } );
    }

    return response;
  };

  const fetchOptionsByPage = (): Promise<{ label: '', options: { label: string; id: 'all' | 'enabled' | 'disabled'; }[] }[]> => {
    return new Promise( resolve => {
      const options: { label: string; id: 'all' | 'enabled' | 'disabled'; }[] = DEFAULT_STATUS;

      resolve( [
        {
          label: '',
          options: options,
        },
      ] );
    } );
  };

  const setFilterDateFunct = ( startDate: Date | null, endDate: Date | null ) => {
    const updatedValue = [];
    if ( startDate != null && endDate != null ) {
      updatedValue.push( startDate, endDate );
    }
    setFilterDate( updatedValue );
  };

  return (
    <Page title={t( 'common:website_diary' )}>
      {
        isFirstRender ?
          <Loading size={ 100 }/>
          :
          <DataChecker clients={allClients} sites={allSites} sectors={allSectors}>
            <FilterProvider>
              <div className="flex w-[calc(100%)] flex-col p-8 mb-8 justify-self-center">

                <div className="flex justify-between items-start mb-6">
                  <FilterButton className={FILTER_WIDTH} icon={FilterIcon}></FilterButton>
                </div>

                <div className='flex gap-5'>
                  {/* ===== Filters ===== */}
                  <FilterPanel>
                    <div className={`flex flex-col ${FILTER_WIDTH} gap-2`}>
                      {
                        clientsOption.length > 1 && (
                          <MultiSelect
                            label={t( 'common:clients' )}
                            icon={<ClientIcon className="text-orange-base" />}
                            placeholder={ t( 'common:search' ) }
                            getValues={ clientsOption }
                            selectedValues={ selectedClients }
                            onChange={ setSelectedClients }
                            required
                          />
                        )
                      }
                      {
                        ( clientsOption.length > 1 || sitesOption.map( item => { return item.options; } ).flat().length > 1 ) && (
                          <MultiSelect
                            label={t( 'common:sites' )}
                            icon={<SiteIcon className="text-orange-base" />}
                            placeholder={ t( 'common:search' ) }
                            getValues={ sitesOption }
                            selectedValues={ selectedSites }
                            onChange={ setSelectedSites }
                            disabled={ selectedClients.length === 0 }
                          />
                        )
                      }
                      {
                        ( clientsOption.length > 1 || sitesOption.map( item => { return item.options; } ).flat().length > 1 || sectorsOption.map( item => { return item.options; } ).flat().length > 1 ) && (
                          <MultiSelect
                            label={t( 'common:sectors' )}
                            icon={<SectorIcon className="text-orange-base" />}
                            placeholder={ t( 'common:search' ) }
                            getValues={ sectorsOption }
                            selectedValues={ selectedSectors }
                            onChange={ setSelectedSectors }
                            disabled={ selectedClients.length === 0 || selectedSites.length === 0 }
                          />
                        )
                      }

                      <MultiSelect
                        label={t( 'translation:event_manager' )}
                        icon={<EventManagerIcon className="text-orange-base" />}
                        placeholder={ t( 'common:search' ) }
                        getValues={ eventManagersOption }
                        selectedValues={ selectedEventManagers }
                        onChange={ setSelectedEventManagers }
                        disabled={ selectedClients.length === 0 || selectedSites.length === 0 || selectedSectors.length === 0 }
                      />

                      <MultiSelect
                        label={t( 'common:theme' )}
                        icon={<ThemeIcon className="text-orange-base" />}
                        placeholder={ t( 'common:search' ) }
                        getValues={ themesOptions }
                        selectedValues={ selectedThemes }
                        onChange={ setSelectedThemes }
                        disabled={ selectedClients.length === 0 || selectedSites.length === 0 || selectedSectors.length === 0 || selectedEventManagers.length === 0 }
                      />

                      <SelectInput
                        label={t( 'common:status' )}
                        fetchOptions={fetchOptionsByPage}
                        onSelect={ selected => {
                          setFilterStatus( selected as ( 'all' | 'enabled' | 'disabled' ) );
                        } }
                        labelPosition="top"
                        selected={filterStatus}
                      />

                      <DatePickerInput
                        textSize='w-full'
                        label={t( 'common:creation_date' )}
                        placeholder={t( 'translation:select_date' )}
                        onChange={setFilterDateFunct}
                        startDate={ filterDate.length === 2 ? filterDate[ 0 ] : null }
                        endDate={ filterDate.length === 2 ? filterDate[ 1 ] : null }
                        customButtons={ [ { label: t( 'common:clear' ), dates: null } ] }
                        startMounthToSee={ dayjs().add( -1, 'month' ).startOf( 'month' ).toDate() }
                        labelPosition='top'
                      />

                    </div>
                  </FilterPanel>

                  {
                    selectedClients.length > 0 ?
                      <GenericTable<DiaryNoteResponse>
                        title={ t( 'translation:notes_list' ) }
                        getData={ fetchDiaryNotes }
                        renderList={ renderList }
                        columns={[
                          {
                            key: 'eventTitle',
                            label: t( 'common:title' ),
                            type: 'string',
                            className: 'px-4 py-3 text-gray-900 font-semibold whitespace-nowrap',
                            getValue: note => { return note.eventTitle || '-'; },
                          },
                          {
                            key: 'value',
                            label: t( 'translation:note' ),
                            type: 'string',
                            className: 'px-4 py-3 text-gray-700 whitespace-nowrap max-w-[300px] truncate',
                            getValue: note => { return note.value || '-'; },
                          },
                          {
                            key: 'authorName',
                            label: t( 'common:author' ),
                            type: 'actions',
                            className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
                            getValue: note => { return <p className='rounded-md flex-center bg-gray-100 px-1 py-0.5'>{ note.authorName || '-' }</p>; },
                          },
                          {
                            key: 'eventManagerName',
                            label: t( 'common:table' ),
                            type: 'string',
                            className: 'px-4 py-3 text-gray-700 whitespace-nowrap max-w-[300px] truncate',
                            getValue: note => { return note.eventManagerName || '-'; },
                          },
                          {
                            key: 'sectorName',
                            label: t( 'common:sector' ),
                            type: 'string',
                            className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
                            getValue: note => { return note.sectorName || '-'; }
                          },
                          {
                            key: 'creationDate',
                            label: t( 'common:creation_date' ),
                            type: 'date',
                            className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
                            getValue: note => { return note.creationDate ? dayjs( note.creationDate ).format( 'L' ) :  '-'; },
                          },
                          {
                            key: 'themeName',
                            label: t( 'common:theme' ),
                            type: 'theme',
                            className: 'px-4 py-3 text-gray-700 whitespace-nowrap gap-2',
                            getValue: note => { return note.theme || '-'; },
                          },
                          {
                            key: 'disable',
                            label: t( 'common:actions' ),
                            type: 'actions',
                            className: 'flex px-4 py-3 text-gray-700 whitespace-nowrap',
                            getValue: note => {
                              return (
                                !note.disable ?
                                  <button
                                    className='w-fit px-2 py-1 rounded hover:brightness-110 bg-red-200 text-red-600 hover:bg-red-300'
                                    onClick={async e => {
                                      e.stopPropagation();
                                      if ( !note.id ) {return;}
                                      await EventService.updateNote( note.id, { disable: !note.disable }, token, dispatch );
                                      setRenderList( renderList + 1 );
                                    }}
                                  >
                                    <div className='flex gap-2 justify-center items-center'><NewsDeleteIcon size={5}/>{t( 'common:disable' )}</div>
                                  </button>
                                  :
                                  <div className='flex gap-2 w-fit px-2 justify-center items-center text-red-600'><NewsDeleteIcon size={5} className='pt-[1.3px]'/>{t( 'common:disabled' )}</div>
                              );
                            },
                          },
                        ]}
                      />
                      :
                      <div className="w-full flex flex-col items-center text-gray-600 text-center italic">
                        <NoDataAvailableIcon size={10} className="text-gray-400 mb-4" />
                        <p>{t( 'common:no_data_to_display' )}</p>
                      </div>
                  }
                </div>

              </div>
            </FilterProvider>
          </DataChecker>
      }
    </Page>
  );


};

export default DiaryNoteView;